import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { OperationGroup, OperationGroupFormData } from '../models/OperationGroup';

interface OperationGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: OperationGroupFormData) => Promise<string>;
  group?: OperationGroup;
}

const OperationGroupDialog: React.FC<OperationGroupDialogProps> = ({
  open,
  onClose,
  onSave,
  group,
}) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (group) {
      setCode(group.id);
      setName(group.name);
      setCity(group.city || '');
      setDescription(group.description || '');
      setIsActive(group.isActive);
    } else {
      setCode('');
      setName('');
      setCity('');
      setDescription('');
      setIsActive(true);
    }
    setSaving(false);
    setError(null);
  }, [group, open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Le nom du groupe est requis');
      return;
    }
    if (!group && !code.trim()) {
      setError('Le code groupe est requis');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        code: group ? undefined : code.trim(),
        name: name.trim(),
        city: city.trim() || undefined,
        description: description.trim() || undefined,
        isActive,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de l’enregistrement du groupe');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{group ? 'Modifier le groupe opérationnel' : 'Ajouter un groupe opérationnel'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!group && (
            <TextField
              label="Code groupe"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              fullWidth
              required
              helperText="Code court unique du groupe. Exemple: kenitra_core"
            />
          )}

          {group && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Code groupe
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {group.id}
              </Typography>
            </Box>
          )}

          <TextField
            label="Nom du groupe"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Ville"
            value={city}
            onChange={(event) => setCity(event.target.value)}
            fullWidth
          />

          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
            }
            label="Groupe actif"
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={22} /> : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OperationGroupDialog;
